import { KeyIcon } from "@heroicons/react/outline";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";

import { loginSchema } from "./schema";

import { useForgotPassword } from "@/api/application-api";
import AuthSubmitButton from "@/components/AuthSubmitButton";
import { Badge, BadgeSize, BadgeVariant } from "@/components/ui/Badge";
import { Button } from "@/components/ui/button";
import { FormInput } from "@/components/ui/FormInput";
import { SocialButton } from "@/components/ui/SocialButton";
import { SocialProvider } from "@/components/ui/SocialButton/constants";
import { AUTH_CONFIG } from "@/constants";
import { useAuth } from "@/context/auth-context";
import { useLogin } from "@/queries/auth/useLogin";
import { useValidateEmail } from "@/queries/auth/useValidateEmail";
import { displayErrorToast } from "@/utils/toast";
import { resolveAuthenticatedLoginRedirect } from "@/utils/postLoginNavigation";

export const RegularLogin = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const [showCaptchaChallenge, setShowCaptchaChallenge] = useState(false);
  const { mutate: login, isLoading: isLoginLoading } = useLogin(verdict => {
    if (verdict === "challenge") {
      setShowCaptchaChallenge(true);
    }
  });
  const { mutate: forgotPassword, isLoading: isForgotPasswordLoading } = useForgotPassword();
  const recaptchaRef = useRef<ReCAPTCHA | null>(null);
  const { isOnPrem, hasSSO, companyName } = AUTH_CONFIG;

  const [showPassword, setShowPassword] = useState(false);
  const [withPassword, setWithPassword] = useState(false);
  const { mutate: validateEmail, isLoading: isValidateEmailLoading } = useValidateEmail();
  const [searchParams] = useSearchParams();
  const isSSO = searchParams.get("sso") === "true";

  useEffect(() => {
    if (!auth?.userToken || !auth?.user) {
      return;
    }

    const redirectPath = resolveAuthenticatedLoginRedirect(auth.userToken, auth.user);
    if (redirectPath) {
      navigate(redirectPath);
    }
  }, [auth?.userToken, auth?.user, navigate]);

  const form = useForm<z.infer<typeof loginSchema>>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      email: isSSO ? (searchParams.get("email") ?? "") : "",
      password: "",
    },
    resolver: zodResolver(loginSchema),
  });

  const handleForgotPasswordClick = async () => {
    const email = form.watch("email");
    const emailSchema = z
      .string()
      .email({ message: "Invalid email format" })
      .nonempty({ message: "Email is required" });
    const emailResult = emailSchema.safeParse(email);

    if (!emailResult.success) {
      form.setError("email", { message: "Please provide a valid email." });
      return;
    }

    forgotPassword({ email });
  };

  const handleCaptchaSuccess = (token: string | null) => {
    if (!token) {
      return;
    }

    setShowCaptchaChallenge(false);

    login(
      {
        email: form.getValues().email,
        password: form.getValues().password,
        recaptcha_response: token,
      },
      {
        onSuccess: () => {
          setShowCaptchaChallenge(false);
        },
      },
    );
  };

  const onSubmit = async (data: z.infer<typeof loginSchema>) => {
    localStorage.setItem("provider", withPassword ? "password" : "email");
    if (!showPassword && (!isOnPrem || hasSSO)) {
      validateEmail(
        { email: data.email },
        {
          onSuccess: response => {
            if (response.authorization_url) {
              window.location.href = response.authorization_url;
              return;
            }

            if (response.valid && !isOnPrem) {
              setShowPassword(true);
            } else if (isOnPrem) {
              displayErrorToast("Please use SSO to log in.");
            } else {
              displayErrorToast("We couldn't find an account with this email.");
            }
          },
        },
      );
      return;
    }

    login(data);
  };

  if (!hasSSO && isOnPrem) {
    return (
      <div className="flex flex-col gap-3">
        <form className="flex flex-col gap-3" onSubmit={form.handleSubmit(onSubmit)}>
          <FormInput
            label="Email"
            placeholder="Email"
            type="text"
            error={form.formState.errors.email?.message}
            {...form.register("email")}
          />
          <FormInput
            label="Password"
            placeholder="Password"
            type="password"
            error={form.formState.errors.password?.message}
            {...form.register("password")}
          />
          {showCaptchaChallenge && (
            <div className="flex h-fit justify-end">
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={import.meta.env.REACT_APP_RECAPTCHA_V2_SITE_KEY}
                onChange={handleCaptchaSuccess}
              />
            </div>
          )}
          <AuthSubmitButton loading={isLoginLoading} text="Login" />
        </form>
        <div className="text-center text-sm font-light text-gray-800">
          Don't have an account yet?{" "}
          <Button variant="inlineLink" onClick={() => navigate("/create-account")}>
            Sign Up
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <form
        className={`flex flex-col ${showPassword ? "gap-3" : ""}`}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <FormInput
          label="Email"
          placeholder="Email"
          disabled={showPassword && !withPassword}
          type="text"
          error={form.formState.errors.email?.message}
          {...form.register("email")}
        />
        {companyName && (
          <div className="mt-1 text-xs text-gray-500">Use your {companyName} email to sign in.</div>
        )}
        {showPassword && (
          <div className="flex flex-col gap-2">
            <FormInput
              label="Password"
              placeholder="Password"
              type="password"
              error={form.formState.errors.password?.message}
              {...form.register("password")}
            />
            <div className="flex justify-between">
              <Button
                variant="link"
                className={`!p-0 text-sm ${"text-[#1D4ED8] hover:text-[#1D4ED8]/90"}`}
                onClick={() => {
                  setShowPassword(false);
                  setWithPassword(false);
                }}
                type="button"
              >
                {withPassword ? "Use different sign in method" : "Enter different email"}
              </Button>
              <Button
                variant="link"
                className={`!p-0 text-sm ${
                  isForgotPasswordLoading
                    ? "text-gray-300"
                    : "text-[#1D4ED8] hover:text-[#1D4ED8]/90"
                }`}
                onClick={handleForgotPasswordClick}
                isLoading={isForgotPasswordLoading}
                disabled={isForgotPasswordLoading}
                type="button"
              >
                Forgot password?
              </Button>
            </div>
          </div>
        )}
        {showCaptchaChallenge && (
          <div className="flex h-fit justify-end">
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={import.meta.env.REACT_APP_RECAPTCHA_V2_SITE_KEY}
              onChange={handleCaptchaSuccess}
            />
          </div>
        )}
        <AuthSubmitButton
          loading={isLoginLoading || isValidateEmailLoading}
          text={
            showPassword
              ? "Login"
              : `Continue with ${isSSO || (isOnPrem && hasSSO) ? "SSO" : "Email"}`
          }
        />
      </form>
      {!showPassword && !isOnPrem && (
        <>
          <div className="flex flex-col gap-2">
            <div className="my-2 flex items-center gap-2">
              <div className="h-[1px] w-full bg-gray-300" />
              <span className="px-2 text-sm font-light text-gray-800">OR</span>
              <div className="h-[1px] w-full bg-gray-300" />
            </div>
            <div className="mb-2 flex flex-col gap-2">
              <Button
                variant="outline"
                size="xl"
                className="relative p-5"
                onClick={() => {
                  setWithPassword(true);
                  setShowPassword(true);
                }}
              >
                {localStorage.getItem("last_login_type") === "password" && !isOnPrem && (
                  <Badge
                    variant={BadgeVariant.BLUE}
                    size={BadgeSize.SM}
                    className="absolute -right-3 -top-2.5 flex items-center gap-2 !text-xs shadow-sm"
                  >
                    Last used
                  </Badge>
                )}
                <div className="flex items-center justify-center gap-3 px-4 py-2.5">
                  <KeyIcon className="h-5 w-5 flex-shrink-0" />
                  <span className="text-sm font-medium">Continue with Password</span>
                </div>
              </Button>
              <SocialButton provider={SocialProvider.google} />
              <SocialButton provider={SocialProvider.github} />
              <SocialButton provider={SocialProvider.gitlab} />
            </div>
          </div>
          <div className="text-center text-sm font-light text-gray-800">
            Don't have an account yet?{" "}
            <Button variant="inlineLink" onClick={() => navigate("/create-account")}>
              Sign Up
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
